import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, BedDouble, Users, IndianRupee, Menu as MenuIcon, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { useT, TKey } from "@/lib/i18n";
import { BrandSymbol } from "@/components/Brand";

const NAV: { to: string; key: TKey; Icon: any; id: string }[] = [
  { to: "/", key: "nav_dashboard", Icon: LayoutDashboard, id: "dashboard" },
  { to: "/beds", key: "nav_beds", Icon: BedDouble, id: "beds" },
  { to: "/tenants", key: "nav_tenants", Icon: Users, id: "tenants" },
  { to: "/rent", key: "nav_rent", Icon: IndianRupee, id: "rent" },
  { to: "/more", key: "nav_more", Icon: MenuIcon, id: "more" },
];

function PropertySwitcher() {
  const { properties, property, selectProperty } = useAuth();
  if (!property) return null;
  return (
    <Select value={property.id} onValueChange={selectProperty}>
      <SelectTrigger data-testid="property-switcher" className="h-10 max-w-[260px] rounded-full border-slate-200 bg-white font-semibold text-slate-800">
        <Building2 className="h-4 w-4 text-emerald-700 shrink-0 mr-1" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {properties.map((p) => (
          <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.id}`}>{p.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BottomNav() {
  const { t } = useT();
  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
      <div className="max-w-md mx-auto grid grid-cols-5">
        {NAV.map(({ to, key, Icon, id }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`nav-tab-${id}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-semibold transition-colors ${isActive ? "text-emerald-700" : "text-slate-500"}`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`grid place-items-center h-8 w-12 rounded-full transition-colors ${isActive ? "bg-emerald-50" : ""}`}>
                  <Icon className="h-5 w-5" />
                </span>
                {t(key)}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function AppShell() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#F6F7F5]">
      <header className="sticky top-0 z-30 h-16 border-b border-slate-200/80 bg-white/85 backdrop-blur-md">
        <div className="max-w-md mx-auto h-full px-4 flex items-center justify-between gap-2">
          <button data-testid="brand-home-btn" onClick={() => nav("/")} className="flex items-center gap-2 shrink-0">
            <BrandSymbol size={32} />
          </button>
          <PropertySwitcher />
        </div>
      </header>
      <main className="max-w-md mx-auto px-4 pt-5 pb-28">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}
